import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, Trash2, ShieldAlert, Sparkles, Plus, Copy, Check, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { SettingsTabs } from "@/components/SettingsTabs";
import { SETTINGS } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

function Section({ title, desc, children }) {
  return (
    <section className="mt-8 rounded-xl border border-border/60 bg-card/40 p-5">
      <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      {desc && <p className="mt-1 text-xs text-muted-foreground">{desc}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ApiKeysSection({ workspaceId }) {
  const [keys, setKeys] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genName, setGenName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/developers/keys", { params: { workspace_id: workspaceId } });
      setKeys(data);
    } catch {
      setKeys([]);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!genName.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/developers/keys", { workspace_id: workspaceId, name: genName.trim() });
      setNewKey(data);
      setGenName("");
      setCopied(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate key");
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/developers/keys/${id}`);
      toast.success("API key revoked");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke key");
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey.full_key);
      setCopied(true);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select the key and copy manually");
    }
  };

  const closeDialog = () => { setGenOpen(false); setNewKey(null); setGenName(""); setCopied(false); };

  return (
    <Section
      title="Developer API keys"
      desc="Let external apps query this workspace's governed semantic layer via POST /api/v1/query. Keys are shown once at creation — store them securely."
    >
      <div data-testid={SETTINGS.apiKeysSection} className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/60 px-3 py-1 text-[11px] text-muted-foreground">
            <Code2 className="h-3.5 w-3.5 text-accent" /> Bearer or X-API-Key auth · rate limited by plan
          </div>
          <Button
            onClick={() => { setNewKey(null); setGenName(""); setGenOpen(true); }}
            data-testid={SETTINGS.apiKeyGenerate}
            className="h-9 rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
          >
            <Plus className="mr-2 h-4 w-4" /> Generate key
          </Button>
        </div>

        {keys === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-accent" /></div>
        ) : keys.length === 0 ? (
          <p data-testid={SETTINGS.apiKeysEmpty} className="rounded-lg border border-dashed border-border/60 bg-background/40 px-4 py-8 text-center text-xs text-muted-foreground">
            No API keys yet. Generate one to start querying Sema from your own apps.
          </p>
        ) : (
          <div className="divide-y divide-border/50 overflow-hidden rounded-lg border border-border/60">
            {keys.map((k) => (
              <div key={k.id} data-testid={SETTINGS.apiKeyRow} className="flex items-center justify-between gap-3 bg-background/40 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{k.name}</span>
                    {k.active ? (
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] uppercase tracking-wider text-emerald-400">Active</span>
                    ) : (
                      <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[9px] uppercase tracking-wider text-red-400">Revoked</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[10px] text-muted-foreground">
                    <code className="text-foreground/70">{k.masked}</code>
                    <span>{k.last_used_at ? `last used ${new Date(k.last_used_at).toLocaleDateString()}` : "never used"}</span>
                  </div>
                </div>
                {k.active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={SETTINGS.apiKeyRevoke}
                    onClick={() => revoke(k.id)}
                    className="h-8 shrink-0 rounded-full text-[10px] uppercase tracking-[0.14em] text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Revoke
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={genOpen} onOpenChange={(o) => (o ? setGenOpen(true) : closeDialog())}>
        <DialogContent data-testid={SETTINGS.apiKeyDialog} className="font-mono sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="h-5 w-5 text-accent" /> {newKey ? "Copy your API key" : "Generate API key"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {newKey
                ? "This is the only time the full key is shown. Store it somewhere safe — you can't view it again."
                : "Give the key a name so you can recognise it later (e.g. the app that will use it)."}
            </DialogDescription>
          </DialogHeader>

          {!newKey ? (
            <div className="space-y-3">
              <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Key name</Label>
              <Input
                data-testid={SETTINGS.apiKeyNameInput}
                value={genName}
                onChange={(e) => setGenName(e.target.value)}
                placeholder="My data app"
                className="h-9 bg-background/60 text-sm"
                onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
              />
              <DialogFooter>
                <Button
                  onClick={generate}
                  disabled={generating || !genName.trim()}
                  className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <code
                  data-testid={SETTINGS.apiKeyValue}
                  className="flex-1 overflow-x-auto rounded-lg border border-border/60 bg-background/80 px-3 py-2.5 text-[11px] text-accent"
                >
                  {newKey.full_key}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  data-testid={SETTINGS.apiKeyCopy}
                  onClick={copyKey}
                  className="h-9 w-9 shrink-0 rounded-lg border-border/80"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="rounded-lg border border-border/50 bg-background/40 p-3 text-[10px] leading-relaxed text-muted-foreground">
                <p className="mb-1 uppercase tracking-[0.16em] text-foreground/70">Use it</p>
                <pre className="overflow-x-auto whitespace-pre-wrap"><code>{`curl -X POST "$BACKEND_URL/api/v1/query" \\
  -H "Authorization: Bearer ${newKey.prefix ? `sema_${newKey.prefix}_…` : "<key>"}" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "Which facility has the most readmissions?"}'`}</code></pre>
              </div>
              <DialogFooter>
                <Button
                  onClick={closeDialog}
                  data-testid={SETTINGS.apiKeyDone}
                  className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

export default function SettingsWorkspace() {
  const { active, refresh, setActive, workspaces } = useWorkspace();
  const navigate = useNavigate();
  const canManage = active && (active.role === "owner" || active.role === "admin");
  const isOwner = active && active.role === "owner";

  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [llm, setLlm] = useState(null);
  const [provider, setProvider] = useState("anthropic");
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [savingLlm, setSavingLlm] = useState(false);

  const loadLlm = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/workspaces/${active.id}/llm-settings`);
      setLlm(data);
      setProvider(data.provider);
      setModel(data.model);
    } catch (err) {
      if (err.response?.status === 403) setLlm({ denied: true });
    }
  }, [active]);

  useEffect(() => {
    setName(active?.name || "");
    loadLlm();
  }, [active, loadLlm]);

  const models = llm?.provider_models?.[provider] || [];

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await api.patch(`/workspaces/${active.id}`, { name });
      toast.success("Workspace renamed");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename");
    } finally {
      setSavingName(false);
    }
  };

  const saveLlm = async (clearKey = false) => {
    setSavingLlm(true);
    try {
      const body = { provider, model, clear_key: clearKey };
      if (!clearKey && apiKey) body.api_key = apiKey;
      const { data } = await api.put(`/workspaces/${active.id}/llm-settings`, body);
      setLlm(data);
      setApiKey("");
      toast.success(clearKey ? "Reverted to platform key" : "LLM settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save LLM settings");
    } finally {
      setSavingLlm(false);
    }
  };

  const deleteWs = async () => {
    try {
      await api.delete(`/workspaces/${active.id}`);
      toast.success("Workspace deleted");
      const next = workspaces.find((w) => w.id !== active.id);
      if (next) setActive(next.id);
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete workspace");
    }
  };

  return (
    <div data-testid={SETTINGS.workspaceRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-3xl px-6 py-10">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Configure <span className="text-foreground">{active?.name}</span>.
        </p>
        <SettingsTabs />

        {!canManage ? (
          <div data-testid={SETTINGS.denied} className="mt-10 rounded-xl border border-border/60 bg-card/40 p-8 text-center text-sm text-muted-foreground">
            <ShieldAlert className="mx-auto mb-3 h-6 w-6 text-amber-400" />
            Workspace settings require an admin or owner role.
          </div>
        ) : (
          <>
            <Section title="Workspace name" desc="Shown in the workspace switcher and shared links.">
              <form onSubmit={saveName} className="flex flex-wrap items-end gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Name</Label>
                  <Input
                    required
                    data-testid={SETTINGS.renameInput}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-9 bg-background/60 text-sm"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={savingName || !name.trim()}
                  data-testid={SETTINGS.renameSave}
                  className="h-9 rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
                >
                  {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" />Save</>}
                </Button>
              </form>
            </Section>

            <Section
              title="AI model"
              desc="Choose the LLM that powers conversational query for this workspace. Leave the key blank to use the Sema platform key."
            >
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border/60 px-3 py-1 text-[11px]" data-testid={SETTINGS.llmSource}>
                <Sparkles className="h-3.5 w-3.5 text-accent" />
                {llm?.source === "workspace"
                  ? <>Using <span className="text-foreground">your own key</span>{llm?.key_hint ? ` (${llm.key_hint})` : ""}</>
                  : <>Using the <span className="text-foreground">Sema platform key</span></>}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Provider</Label>
                  <Select value={provider} onValueChange={(v) => { setProvider(v); setModel((llm?.provider_models?.[v] || [])[0] || ""); }}>
                    <SelectTrigger data-testid={SETTINGS.llmProvider} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(llm?.provider_models || { anthropic: 1 }).map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Model</Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger data-testid={SETTINGS.llmModel} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {models.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">API key (optional)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    data-testid={SETTINGS.llmKey}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={llm?.has_key ? "•••• stored — leave blank to keep" : "Use platform key (leave blank)"}
                    className="h-9 bg-background/60 text-sm"
                  />
                  <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Button
                  onClick={() => saveLlm(false)}
                  disabled={savingLlm || !model}
                  data-testid={SETTINGS.llmSave}
                  className="h-9 rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
                >
                  {savingLlm ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" />Save model</>}
                </Button>
                {llm?.has_key && (
                  <Button
                    variant="outline"
                    onClick={() => saveLlm(true)}
                    disabled={savingLlm}
                    data-testid={SETTINGS.llmClearKey}
                    className="h-9 rounded-full border-border/80 text-xs uppercase tracking-[0.16em]"
                  >
                    Use platform key
                  </Button>
                )}
              </div>
            </Section>

            <ApiKeysSection workspaceId={active.id} />

            <Section title="Danger zone" desc="Deleting a workspace removes its members and settings. Data sources and history are detached.">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={!isOwner}
                    data-testid={SETTINGS.deleteWorkspace}
                    className="h-9 rounded-full border-red-500/40 text-xs uppercase tracking-[0.16em] text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete workspace
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="font-mono">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete “{active?.name}”?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the workspace and all memberships. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={SETTINGS.deleteConfirm}
                      onClick={deleteWs}
                      className="bg-red-500 text-white hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              {!isOwner && <p className="mt-2 text-[11px] text-muted-foreground">Only the workspace owner can delete it.</p>}
            </Section>
          </>
        )}
      </main>
    </div>
  );
}
